import React, { useState } from 'react'
import { Tag, Divider } from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { ILocation } from '@api'
import { MultiCompanyLocationSelect, IconButton, Button, Toaster, Confirmation } from '@ui'
import { useReload } from '@hooks/reloadContext'
import { ApolloError } from 'apollo-client'
import { useEffect } from 'react'

export default function LocationSetting({
  disabledLocations,
  locationList,
  createButtonText,
  createContent,
  deleteButtonText,
  content,
  editable = false,
  saveConfirmContent = () => null,
  deleteConfirmContent,
  deleteLocation,
  onAddClose,
  canExpand = false,
}: {
  disabledLocations: ILocation[]
  locationList: { id?: string; locations: ILocation[] }[]
  createButtonText: string
  createContent: (
    locs: ILocation[],
    disabledLocations: ILocation[]
  ) => {
    tooltipText?: string
    enableTooltip?: boolean
    saveParams?: any
    saveDisabled: boolean
    newLocationText: string
    element: JSX.Element
    addLocation: (locs: ILocation[]) => Promise<void>
    disabledLocationIds: string[]
  }
  deleteButtonText: string
  content: (data: any) => {
    locationTitle: string
    cancelEdit: () => void
    saveLocation: (locs: ILocation[]) => Promise<void>
    element: JSX.Element
    saveDisabled: boolean
    saveParams?: any
  }
  editable?: boolean
  saveConfirmContent?: (params: any) => JSX.Element | null
  deleteConfirmContent: (params: any) => JSX.Element | null
  deleteLocation: (params: any) => Promise<void>
  onAddClose: () => void
  canExpand?: boolean
}) {
  const [isCreating, setIsCreating] = useState(false)
  const [editInProgress, setEditInProgress] = useState(false)

  const dLocations = (indx: number) => {
    return locationList.reduce(
      (a: any | string[], { locations }: { id?: string; locations: ILocation[] }, i) => {
        return i === indx ? [...a] : [...a, ...locations.map(({ id }) => id)]
      },
      [] as string[]
    ) as unknown as string[]
  }

  return (
    <>
      <div className="my-4">
        {locationList.map((loc, i) => (
          <LocationModel
            key={i}
            id={'i' + i}
            index={i}
            location={loc}
            disabledIds={[...dLocations(i)]}
            deleteButtonText={deleteButtonText}
            content={content}
            editInProgress={editInProgress}
            setEditInProgress={setEditInProgress}
            editable={editable}
            saveConfirmContent={saveConfirmContent}
            deleteConfirmContent={deleteConfirmContent}
            deleteLocation={deleteLocation}
            canExpand={canExpand}
          />
        ))}

        {isCreating ? (
          <LocationCreate
            disabledLocations={disabledLocations}
            onClose={() => {
              setIsCreating(false)
              setEditInProgress(false)
              onAddClose()
            }}
            createContent={createContent}
            saveConfirmContent={saveConfirmContent}
          />
        ) : (
          <Button
            className="my-3"
            minimal
            blue
            disabled={editInProgress}
            iconName="plus"
            text={createButtonText}
            onClick={() => {
              setIsCreating(true)
              setEditInProgress(true)
            }}
          />
        )}
      </div>
    </>
  )
}

function LocationModel({
  id,
  location,
  disabledIds,
  index,
  deleteButtonText,
  content,
  editable,
  editInProgress,
  setEditInProgress,
  saveConfirmContent,
  deleteConfirmContent,
  deleteLocation,
  canExpand,
}: {
  id: string
  location: any
  index: number
  disabledIds: string[]
  deleteButtonText: string
  editInProgress: boolean
  setEditInProgress: (param: boolean) => void
  content: (data: any) => {
    locationTitle: string
    cancelEdit: () => void
    saveLocation: (locs: ILocation[]) => Promise<void>
    element: JSX.Element
    saveDisabled: boolean
    tooltipText?: string
    enableTooltip?: boolean
    saveParams?: any
  }
  editable: boolean
  saveConfirmContent: (params: any) => JSX.Element | null
  deleteConfirmContent: (params: any) => JSX.Element | null
  deleteLocation: (params: any) => Promise<void>
  canExpand: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [locs, setLocs] = useState<ILocation[]>(location.locations || [])
  const [showTooltip, setShowTooltip] = useState(false)
  const [saveContent, setSaveContent] = useState<JSX.Element | null>(null)

  const reload = useReload()

  useEffect(() => {
    setLocs(location.locations)
  }, [location.locations])

  const {
    locationTitle,
    saveLocation,
    cancelEdit,
    element,
    saveDisabled,
    tooltipText,
    enableTooltip = false,
    saveParams,
  } = content({
    ...location,
    isEditing,
    isOpen,
    index,
  })

  const deleteContent = deleteConfirmContent({ ...location, ...saveParams, locs, index })
  const onDelete = () => {
    return deleteLocation({ id, ...location })
      .then(() => {
        setEditInProgress(false)
        reload.locationHealthChecks()
      })
      .catch((error: ApolloError) => {
        Toaster.fail({ error })
      })
  }
  const onSave = () => {
    return saveLocation(locs)
      .then(() => {
        setIsSaving(false)
        setIsEditing(false)
        setEditInProgress(false)
      })
      .catch((error: ApolloError) => {
        setIsSaving(false)
        Toaster.fail({ error })
      })
  }

  return (
    <div className="my-4" key={id}>
      <div className="flex justify-between py-3">
        <div>{locationTitle}</div>
        {canExpand && (
          <IconButton
            minimal
            iconName={isOpen ? 'chevron-up' : 'chevron-down'}
            onClick={() => setIsOpen(!isOpen)}
            tooltip={isOpen ? 'Hide messages' : 'Show messages'}
          />
        )}
      </div>
      {isEditing ? (
        <MultiCompanyLocationSelect
          values={locs}
          onChange={setLocs}
          noTitle
          hideButton={!isEditing}
          disabledIds={[...disabledIds]}
        />
      ) : (
        locs.map(({ city }, i) => (
          <Tag key={i} round minimal style={{ padding: 10, fontSize: 14 }}>
            {city}
          </Tag>
        ))
      )}

      <div>
        {element}
        <div className="flex justify-between mt-2">
          {deleteContent ? (
            <Confirmation noIcon text={deleteContent} cancelable onConfirm={onDelete}>
              <Button text={deleteButtonText} iconName="trash" intent="danger" minimal />
            </Confirmation>
          ) : (
            <Button
              text={deleteButtonText}
              iconName="trash"
              intent="danger"
              minimal
              onClick={onDelete}
            />
          )}
          {editable &&
            (isEditing ? (
              <div
                className="flex"
                onMouseEnter={() => {
                  setSaveContent(saveConfirmContent({ ...location, ...saveParams, locs, index }))
                  setShowTooltip(true)
                }}
                onMouseLeave={() => {
                  setSaveContent(null)
                  setShowTooltip(false)
                }}
              >
                <Button
                  minimal
                  className=" mr-3"
                  text="Cancel"
                  iconName="times"
                  onClick={() => {
                    setIsEditing(false)
                    setEditInProgress(false)
                    cancelEdit()
                  }}
                />
                {saveContent ? (
                  <Confirmation
                    noIcon
                    text={saveContent}
                    cancelable
                    onConfirm={() => {
                      setIsSaving(true)
                      onSave()
                    }}
                  >
                    <Tooltip2
                      canEscapeKeyClose
                      placement="top"
                      content={<div style={{ width: 400 }}>{tooltipText}</div>}
                      disabled={!tooltipText || !enableTooltip}
                      isOpen={showTooltip}
                    >
                      <div className="ml-3">
                        <Button
                          minimal
                          text="Save"
                          intent="primary"
                          iconName="check"
                          loading={isSaving}
                          disabled={saveDisabled}
                        />
                      </div>
                    </Tooltip2>
                  </Confirmation>
                ) : (
                  <Tooltip2
                    placement="top"
                    canEscapeKeyClose
                    content={<div style={{ width: 400 }}>{tooltipText}</div>}
                    disabled={!tooltipText || !enableTooltip}
                    isOpen={showTooltip}
                  >
                    <Button
                      minimal
                      text="Save"
                      intent="primary"
                      iconName="check"
                      loading={isSaving}
                      disabled={saveDisabled}
                      onClick={() => {
                        setIsSaving(true)
                        onSave()
                      }}
                    />
                  </Tooltip2>
                )}
              </div>
            ) : (
              <Button
                minimal
                blue
                text="Edit"
                disabled={editInProgress}
                iconName="pencil"
                onClick={() => {
                  setIsOpen(true)
                  setIsEditing(true)
                  setEditInProgress(true)
                }}
              />
            ))}
        </div>
      </div>
      <Divider />
    </div>
  )
}

function LocationCreate({
  onClose,
  disabledLocations = [],
  createContent,
  saveConfirmContent,
}: {
  onClose: () => void
  disabledLocations: ILocation[]
  createContent: (
    locs: ILocation[],
    disabledLocations: ILocation[]
  ) => {
    saveParams?: any
    tooltipText?: string
    enableTooltip?: boolean
    saveDisabled: boolean
    newLocationText: string
    element: JSX.Element
    addLocation: (locs: ILocation[]) => Promise<void>
    disabledLocationIds: string[]
  }
  saveConfirmContent: (params: any) => JSX.Element | null
}) {
  const [locs, setLocs] = useState<ILocation[]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [saveContent, setSaveContent] = useState<JSX.Element | null>(null)

  const {
    saveParams = null,
    element,
    addLocation,
    disabledLocationIds,
    newLocationText,
    saveDisabled,
    tooltipText,
    enableTooltip = false,
  } = createContent(locs, disabledLocations)

  return (
    <div className="my-3">
      <div className="mb-3">{newLocationText}</div>

      <MultiCompanyLocationSelect
        values={locs}
        onChange={setLocs}
        noTitle
        disabledIds={[...disabledLocationIds]}
        onSelectAll={(locations) => {
          if (locations.length === locs.length) {
            setLocs([])
            return
          }
          setLocs(locations)
        }}
      />

      <div>{element}</div>

      <div className="flex justify-end">
        <Button className="mr-3" minimal text="Cancel" iconName="times" onClick={() => onClose()} />
        {saveContent ? (
          <Confirmation
            noIcon
            text={saveContent}
            cancelable
            onConfirm={() => {
              setIsSaving(true)
              addLocation(locs)
                .then(() => {
                  onClose()
                })
                .catch((error: ApolloError) => {
                  setIsSaving(false)
                  Toaster.fail({ error })
                })
            }}
          >
            <Tooltip2
              canEscapeKeyClose
              placement="top"
              content={<div style={{ width: 400 }}>{tooltipText}</div>}
              disabled={!tooltipText || !enableTooltip}
              isOpen={showTooltip}
            >
              <div
                className="ml-3"
                onMouseOver={() => {
                  setSaveContent(
                    saveConfirmContent({ ...saveParams, locations: [...locs], isCreate: true })
                  )
                  setShowTooltip(true)
                }}
                onMouseOut={() => {
                  setSaveContent(null)
                  setShowTooltip(false)
                }}
              >
                <Button
                  minimal
                  text="Save"
                  iconName="check"
                  intent="primary"
                  loading={isSaving}
                  disabled={saveDisabled}
                />
              </div>
            </Tooltip2>
          </Confirmation>
        ) : (
          <Tooltip2
            placement="top"
            canEscapeKeyClose
            content={<div style={{ width: 400 }}>{tooltipText}</div>}
            disabled={!tooltipText || !enableTooltip}
            isOpen={showTooltip}
          >
            <div
              className="ml-3"
              onMouseOver={() => {
                setSaveContent(
                  saveConfirmContent({ ...saveParams, locations: [...locs], isCreate: true })
                )
                setShowTooltip(true)
              }}
              onMouseOut={() => {
                setSaveContent(null)
                setShowTooltip(false)
              }}
            >
              <Button
                minimal
                text="Save"
                iconName="check"
                intent="primary"
                loading={isSaving}
                disabled={saveDisabled}
                onClick={() => {
                  setIsSaving(true)
                  addLocation(locs)
                    .then(() => {
                      onClose()
                    })
                    .catch((error: ApolloError) => {
                      setIsSaving(false)
                      Toaster.fail({ error })
                    })
                }}
              />
            </div>
          </Tooltip2>
        )}
      </div>
    </div>
  )
}
